import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Form, Input, Button, Layout, Typography, message } from 'antd';
import { getCurrentUser } from 'aws-amplify/auth';
import moment from 'moment';
import { getPreSignedURLAPI, uploadToS3 } from '../apis/upload';
import { getUser } from '../functions/userFuncs';

const { Content } = Layout;
const { Title } = Typography;

const UpdateSearchPage = ({ }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const proofOfPurchaseRef = useRef<HTMLInputElement | null>(null);
  let originalReport = useRef(null);

  const [loading, setLoading] = useState(true);
  const [updatingItem, setUpdatingItem] = useState(false);
  const [updatingReport, setUpdatingReport] = useState(false);
  const [openedItemIndex, setOpenedItemIndex] = useState<number | null>(null);
  const [selectedItem, setSelectedItem] = useState<any>();
  const [proofOfPurchaseToUpload, setProofOfPurchaseToUpload] = useState<any>();
  const [userReports, setUserReports] = useState<any>([]);
  const [selectedReport, setSelectedReport] = useState<any>();

  useEffect(() => {
    getUser()
      .then(user => {
        if (user)
          return axios.get(
            `${process.env.REACT_APP_INSTANCE}/api/report/${user.attributes?.email}`
          );
      })
      .then((res: any) => {
        if (res) {
          setUserReports(res.data.userReports);

          // Automatically open the last report and the last item in that report
          const lastReport = res.data.userReports[res.data.userReports.length - 1];
          if (lastReport) {
            setSelectedReport(lastReport);
            console.log('lastReport:', lastReport);
            originalReport.current = JSON.parse(JSON.stringify(lastReport));

            const lastItemIndex = lastReport._items.length - 1;
            const lastItem = lastReport._items[lastItemIndex];
            setOpenedItemIndex(lastItemIndex);
            setSelectedItem(lastItem);
          }
        }
      })
      .finally(() => setLoading(false));
  }, []);


  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value, name } = event.target;

    if (
      name === 'proofOfPurchase' &&
      event.target instanceof HTMLInputElement &&
      event.target.files
    ) {
      const files = event.target.files;

      setProofOfPurchaseToUpload(files[0]);

      setSelectedItem({
        ...selectedItem,
        proofOfPurchase: {
          link: URL.createObjectURL(files[0]),
          type: files[0].type.includes('pdf') ? 'pdf' : 'image',
        },
      });
    } else {
      setSelectedItem({
        ...selectedItem,
        [name]: name === 'termsAndConditionsAccepted' ? true : value,
      });
    }
  };

  const handleReportChange = async (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value, name } = event.target;

    setSelectedReport({
      ...selectedReport,
      [name]:
        name === 'termsAndConditionsAccepted'
          ? value === '1'
            ? true
            : false
          : value,
    });
  };

  const updateItem = async (item: any) => {
    const response = await axios.patch(
      `${process.env.REACT_APP_INSTANCE}/api/item`,
      item
    );
    return response.data;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (updatingItem) return;
    const itemData = JSON.parse(JSON.stringify(selectedItem));
    setUpdatingItem(true);
    // else if (!proofOfPurchaseToUpload && !selectedItem.proofOfPurchase?.link)
    //   return message.error('Please attach proof of purchase!');

    let attachment;
    if (proofOfPurchaseToUpload) {
      const presignedRes = await getPreSignedURLAPI({
        fileType: proofOfPurchaseToUpload.type,
        fileName: proofOfPurchaseToUpload.name,
      });

      if (presignedRes?.data) {
        const s3Response = await uploadToS3(
          presignedRes.data.url,
          proofOfPurchaseToUpload
        );
        if (s3Response) {
          attachment = s3Response?.config?.url?.split('?')[0];
          itemData.proofOfPurchase = {
            link: attachment,
            type: selectedItem.proofOfPurchase.type,
          };
        }
      }
    }

    const response = await updateItem(itemData);
    if (response) {
      message.success(response.message);
      if (openedItemIndex !== null) {
        const updatedItems = [...selectedReport._items];
        updatedItems[openedItemIndex] = response.updatedItem;

        const selectedReportIndex = userReports.findIndex(
          (report: any) => report._id === selectedReport._id
        );
        if (selectedReportIndex !== -1 && userReports[selectedReportIndex]) {
          userReports[selectedReportIndex]._items = updatedItems;
          setUserReports([...userReports]);
        }
      }
    }

    setUpdatingItem(false);
  };

  const updateReport = async () => {
    await axios.patch(
      `${process.env.REACT_APP_INSTANCE}/api/report`,
      selectedReport
    );
    message.success('Report updated successfully!');
  };

  const proceedToPayment = async (event: React.FormEvent) => {
    event.preventDefault();
    if (updatingReport) return;

    // if (!selectedReport.termsAndConditionsAccepted)
    //   return message.error('Please agree to terms and conditions!');
    setUpdatingReport(true);
    if (
      originalReport.current &&
      JSON.stringify(originalReport.current) !== JSON.stringify(selectedReport)
    )
      await updateReport();

    if (
      !!selectedReport.planExpiry &&
      new Date(selectedReport.planExpiry) > new Date()
    ) {
      try {
        const user = await getCurrentUser();
        if (user) {
          message.success(`Report already submitted!`);
          navigate('/status');
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Error submitting bike report:', error);
        message.error(
          'There was an error submitting your bike report. Please try again.'
        );
      }
    } else {
      setUpdatingReport(false);
      navigate(`/payment/${selectedReport?._id}`);
    }
  };

  const downloadItem = () => {
    const link = document.createElement('a');
    link.href = selectedItem.proofOfPurchase.link;
    link.download = `invoice.${selectedItem.proofOfPurchase.type === 'pdf' ? 'pdf' : 'png'
      }`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Layout>
      <Content style={{ padding: '50px', maxWidth: '600px', margin: '0 auto', }}>
        {/* <Title level={2}>Additional Information</Title> */}

        {loading ? (
          <div
            style={{
              width: '100%',
              height: '100vh',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div className='loader'></div>
          </div>
        ) : (
          userReports?.map((report: any) => (
            <div
              style={{
                width: '100%',
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  background: '#FFBF23',
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.25rem 1rem',
                  borderRadius: '0.25rem',
                  alignItems: 'center',
                  marginBottom: '0.5rem',
                  cursor: 'pointer',
                  minWidth: '360px',
                }}
                onClick={() => {
                  setSelectedReport((pS: any) =>
                    pS?._id === report._id ? null : report
                  );
                  originalReport.current = JSON.parse(JSON.stringify(report));
                }}
              >
                <h4 style={{ margin: '0' }}>{`Report for: ${report._items[0]?.category || 'Item'}`}</h4>

                <i
                  className={`fa-solid fa-chevron-${selectedItem?._id === report._id ? 'up' : 'down'
                    }`}
                ></i>
              </div>
              {selectedReport?._id === report._id && (
                <>
                  {report._items.map((item: any, index: number) => (
                    <div style={{ width: '90%' }}>
                      <div
                        style={{
                          background: '#FFBF23',
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '0.25rem 1rem',
                          borderRadius: '0.25rem',
                          alignItems: 'center',
                          marginBottom: '0.5rem',
                          cursor: 'pointer',
                          width: '100%',
                        }}
                        onClick={() => {
                          if (openedItemIndex === index) {
                            setOpenedItemIndex(null); // Close the item if it's already open
                            setSelectedItem(null);
                          } else {
                            setOpenedItemIndex(index); // Open the clicked item
                            setSelectedItem(JSON.parse(JSON.stringify(report._items[index])));
                          }
                        }}
                      >
                        <h4 style={{ margin: '0' }}>{`${item.category || ''} ${item?.brand
                          }`}</h4>
                        <i
                          className={`fa-solid fa-chevron-${index === openedItemIndex ? 'up' : 'down'
                            }`}
                        ></i>
                      </div>
                      {index === openedItemIndex && (
                        <Form layout='vertical' onSubmitCapture={handleSubmit}>
                          <Form.Item label='Brand of stolen item'>
                            <Input
                              name='brand'
                              value={selectedItem?.brand}
                              onChange={handleChange}
                            />
                          </Form.Item>
                          <Form.Item label='Model of lost item'>
                            <Input
                              name='model'
                              value={selectedItem?.model}
                              onChange={handleChange}
                            />
                          </Form.Item>
                          <Form.Item label='Colour of item'>
                            <Input
                              name='color'
                              value={selectedItem?.color}
                              onChange={handleChange}
                            />
                          </Form.Item>
                          <Form.Item label='Serial number'>
                            <Input
                              name='serialNumber'
                              value={selectedItem?.serialNumber}
                              onChange={handleChange}
                            />
                          </Form.Item>
                          <Form.Item label='Description'>
                            <Input.TextArea
                              name='description'
                              value={selectedItem?.description}
                              onChange={handleChange}
                            />
                          </Form.Item>
                          <Form.Item label='Proof of Purchase'>
                            <input
                              type='file'
                              accept='image/jpeg,image/png,application/pdf'
                              name='proofOfPurchase'
                              onChange={handleChange}
                              style={{ display: 'none' }}
                              ref={proofOfPurchaseRef}
                            />

                            <div
                              style={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                              }}
                            >
                              <div className='proofOfPurchaseBox'>
                                {selectedItem?.proofOfPurchase?.link && (
                                  <div className='proofOfPurchaseBox__item'>
                                    {selectedItem?.proofOfPurchase?.type ===
                                      'image' ? (
                                      <img
                                        src={selectedItem.proofOfPurchase.link}
                                        alt=''
                                      />
                                    ) : (
                                      <div className='proofOfPurchaseBox__invoice'>
                                        Invoice.pdf
                                      </div>
                                    )}
                                  </div>
                                )}

                                <div className='proofOfPurchaseBox__default'>
                                  <i
                                    className='fa-solid fa-upload'
                                    style={{ color: 'green' }}
                                    onClick={() => {
                                      if (proofOfPurchaseRef.current) {
                                        proofOfPurchaseRef.current.click();
                                      }
                                    }}
                                  ></i>
                                  {selectedItem?.proofOfPurchase?.link && (
                                    <i
                                      className='fa-solid fa-download'
                                      style={{ color: 'skyblue' }}
                                      onClick={downloadItem}
                                    ></i>
                                  )}
                                </div>
                              </div>
                            </div>
                          </Form.Item>

                          <Form.Item>
                            <Button type='primary' htmlType='submit'>
                              {updatingItem ? (
                                <span className='loaderSm'></span>
                              ) : (
                                'Update Item Details'
                              )}
                            </Button>
                          </Form.Item>
                        </Form>
                      )}
                    </div>
                  ))}

                  <Form layout='vertical' onSubmitCapture={proceedToPayment}>
                    <Form.Item label='Location where you think your item was stolen from'>
                      <Input
                        name='location'
                        value={selectedReport?.location}
                        onChange={handleReportChange}
                      />
                    </Form.Item>
                    <Form.Item label='Date and time item was stolen'>
                      <Input
                        type='date'
                        name='dateTimeStolen'
                        value={moment(new Date(selectedReport?.dateTimeStolen))
                          .format('YYYY-MM-DD')
                          .toString()}
                        onChange={handleReportChange}
                      />
                    </Form.Item>
                    <Form.Item label='Any additional information'>
                      <Input.TextArea
                        name='additionalInfo'
                        value={selectedReport?.additionalInfo}
                        onChange={handleReportChange}
                      />
                    </Form.Item>
                    <Form.Item>
                      <Button type='primary' htmlType='submit'>
                        {updatingReport ? (
                          <div className='loaderSm'></div>
                        ) : (
                          'Update Report Details'
                        )}
                      </Button>
                    </Form.Item>
                  </Form>
                </>
              )}
            </div>
          ))
        )}
      </Content>
    </Layout>
  );
};

export default UpdateSearchPage;
